import { alpha } from '@mui/material/styles'

import { dsColorNew, dataColors } from '@palette'


// Series colours of the My Business charts, in order: this period, the comparison, then the rest
export const MB_CHART_COLORS = [
	dsColorNew.webBlue500, dataColors.cyanSoft, dsColorNew.tableHead, dsColorNew.cyan, dsColorNew.warningBar,
	dataColors.violet500, dataColors.emerald500, dataColors.red500, dsColorNew.textSubtle, dataColors.pink500,
]

// Chart legend below on the left with rounded marks, as the app's other charts (amCharts)
export const mbLegendBottom = {
	legend: {
		position: { vertical: 'bottom', horizontal: 'left' },
		direction: 'row',
		itemMarkWidth: 14, itemMarkHeight: 14, markGap: 8, itemGap: 22,
		padding: { top: 0, bottom: 0, left: 0, right: 0 },
		labelStyle: { fontSize: 13 },
	},
}

// Axis figures shortened as on the other charts: 250000 → 250k
export const mbCompact = (v) => v == null ? '' : new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(v).toLowerCase()

// The controls of a section (tabs, the metric line, view toggles) in one white strip with the cards'
// border, instead of loose on the page grey
export const mbSectionBarSx = {
	display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5,
	px: 1.75, py: 1.25, borderRadius: '12px',
	backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`,
}

// What the cards, tables, selectors, chips and notices of the module look like, as in the rest of the app.
// Applied once around each page so the widgets keep their own markup.
export const mbScopeSx = {
	// Cards: white, soft border, the app's 16px corners
	'& .MuiPaper-root:not(.MuiAccordion-root):not(.MuiTableContainer-root)': {
		border: `1px solid ${dsColorNew.grayBorder}`,
		borderRadius: '16px',
		boxShadow: `0 1px 2px ${alpha(dsColorNew.navy, 0.04)}`,
	},
	'& .MuiPaper-root .MuiPaper-root:not(.MuiAccordion-root)': { boxShadow: 'none', borderRadius: '12px' },
	'& .MuiAccordion-root': {
		border: `1px solid ${dsColorNew.grayBorder}`,
		borderRadius: '12px !important',
		boxShadow: 'none',
		mb: 1.25,
		'&:before': { display: 'none' },
		'&.Mui-expanded': { m: '0 0 10px' },
	},
	// Tables: the navy header of the app's tables, quiet rows
	'& .MuiTableContainer-root': { borderRadius: '10px', border: `1px solid ${dsColorNew.grayBorder}`, boxShadow: 'none' },
	'& .MuiTableHead-root .MuiTableCell-root': {
		backgroundColor: dsColorNew.tableHead,
		color: `${dsColorNew.white} !important`,
		fontSize: 12.5,
		fontWeight: 600,
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		py: 1.25,
		'& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active': { color: dsColorNew.white },
		'& .MuiTableSortLabel-icon': { color: `${alpha(dsColorNew.white, 0.7)} !important` },
		'& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'inherit' },
	},
	'& .MuiTableBody-root .MuiTableCell-root': { fontSize: 13, color: dsColorNew.textDark, borderColor: dataColors.tableLine },
	'& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) > .MuiTableCell-root': { backgroundColor: dsColorNew.tableRowAlt },
	'& .MuiTableBody-root .MuiTableRow-hover:hover > .MuiTableCell-root': { backgroundColor: dsColorNew.hoverBgLight },
	'& .MuiTableBody-root .MuiTableRow-root:last-of-type > .MuiTableCell-root': { borderBottom: 'none' },
	// Two-or-more option toggles as the app's pill selector
	'& .MuiToggleButtonGroup-root': {
		gap: '4px', p: '3px', borderRadius: '10px',
		backgroundColor: dsColorNew.pillTrackBg, border: `1px solid ${dsColorNew.grayBorder}`,
		'& .MuiToggleButtonGroup-grouped': {
			m: 0, border: '1.5px solid transparent !important', borderRadius: '8px !important',
		},
		'& .MuiToggleButton-root': {
			px: 1.5, py: 0.6, lineHeight: 1.2, textTransform: 'none', fontSize: 12.5, fontWeight: 500, color: dsColorNew.textMuted,
			'&:hover': { backgroundColor: 'transparent', color: dsColorNew.textDark },
			'&.Mui-selected, &.Mui-selected:hover': {
				backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue, fontWeight: 600,
				borderColor: `${dsColorNew.appBlue} !important`,
			},
			'&.Mui-disabled': { color: dsColorNew.textSubtle },
		},
	},
	// Chips: soft tones instead of solid colours
	'& .MuiChip-root': { fontWeight: 600, borderRadius: '10px' },
	'& .MuiChip-filled.MuiChip-colorDefault': { backgroundColor: dsColorNew.pillTrackBg, color: dsColorNew.textDark },
	'& .MuiChip-filled.MuiChip-colorSuccess': { backgroundColor: dsColorNew.successBg, color: dsColorNew.successText },
	'& .MuiChip-filled.MuiChip-colorError': { backgroundColor: dsColorNew.dangerBg, color: dsColorNew.dangerRed },
	'& .MuiChip-filled.MuiChip-colorWarning': { backgroundColor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber },
	'& .MuiChip-filled.MuiChip-colorPrimary, & .MuiChip-filled.MuiChip-colorInfo': { backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue },
	'& .MuiChip-filled .MuiChip-label *': { color: 'inherit' },
	'& .MuiChip-outlined': { borderColor: dsColorNew.grayBorder, color: dsColorNew.textDark },
	'& .MuiChip-outlined.MuiChip-colorWarning': { borderColor: dsColorNew.noteAmberBorder, color: dsColorNew.noteAmber, backgroundColor: dsColorNew.noteAmberBg },
	'& .MuiChip-outlined.MuiChip-colorError': { borderColor: dsColorNew.dangerBorder, color: dsColorNew.dangerRed, backgroundColor: dsColorNew.dangerBg },
	'& .MuiChip-outlined.MuiChip-colorSuccess': { borderColor: dataColors.green200, color: dsColorNew.successText, backgroundColor: dsColorNew.successBg },
	'& .MuiChip-outlined.MuiChip-colorPrimary, & .MuiChip-outlined.MuiChip-colorInfo': { borderColor: dsColorNew.noteInfoBorder, color: dsColorNew.appBlue, backgroundColor: dsColorNew.pillActiveBg },
	// Share bars and progress
	'& .MuiLinearProgress-determinate': { height: '8px !important', borderRadius: '4px', backgroundColor: dsColorNew.pillTrackBg },
	'& .MuiLinearProgress-determinate .MuiLinearProgress-bar': { borderRadius: '4px', backgroundColor: dsColorNew.webBlue500 },
	'& .MuiLinearProgress-indeterminate': { backgroundColor: dsColorNew.pillActiveBg, '& .MuiLinearProgress-bar': { backgroundColor: dsColorNew.appBlue } },
	'& .MuiCircularProgress-root': { color: dsColorNew.appBlue },
	// Buttons inside the widgets
	'& .MuiButton-root': { textTransform: 'none', borderRadius: '8px', boxShadow: 'none', fontWeight: 500 },
	'& .MuiButton-contained': { backgroundColor: dsColorNew.appBlue, '&:hover': { backgroundColor: dsColorNew.appBlueHover, boxShadow: 'none' } },
	'& .MuiButton-outlined': {
		color: dsColorNew.textDark, borderColor: dsColorNew.grayBorder, backgroundColor: dsColorNew.white,
		'& .MuiButton-startIcon': { color: dsColorNew.textMuted },
		'&:hover': { borderColor: dsColorNew.appBlue, backgroundColor: dsColorNew.hoverBgLight },
		'&.Mui-disabled': { color: dsColorNew.textSubtle, '& .MuiButton-startIcon': { color: dsColorNew.textSubtle } },
	},
	// Chart axes, grid and legend in the app's greys
	'& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: `${dsColorNew.grayBorder} !important` },
	'& .MuiChartsAxis-tickLabel': { fill: `${dsColorNew.textMuted} !important`, fontSize: '11px !important' },
	'& .MuiChartsAxis-label': { fill: `${dsColorNew.textMuted} !important` },
	'& .MuiChartsGrid-line': { stroke: dsColorNew.pillTrackBg },
	'& .MuiChartsLegend-series text': { fill: `${dsColorNew.textDark} !important`, fontSize: '12.5px !important' },
	'& .MuiChartsLegend-mark': { rx: 4 },
	// Columns with rounded tops, areas as soft fills under their line, as the app's other charts
	'& .MuiBarElement-root': { rx: 4 },
	'& .MuiAreaElement-root': { fillOpacity: 0.22 },
	'& .MuiLineElement-root': { strokeWidth: 2 },
}
